<template>
  <div class="grid-x grid-padding-x">
    <div class="small-24 medium-12 large-24 cell" v-if="null !== cartTotals">
      <h4>{{ $t('Overview of your order') }}</h4>
      <div class="summary-block">
        <span class="_loading" v-show="!totalsAvailable"></span>
        <div class="summary-overview" v-show="totalsAvailable">
          <h5>{{ $t('Items') }}</h5>
          <ul class="cart__items">
            <li v-for="cartItem in cartItems">
              <span>{{ `${cartItem.qty}x ${cartItem.name}` }}</span>
              <span>{{ cartItem.row_total_incl_tax.priceFormat() }}</span>
            </li>
          </ul>
          <h5>{{ $t('Cart totals') }}</h5>
          <ul class="cart__totals">
            <li>
              <span>{{ $t('Subtotal') }}</span>
              <span>{{ getPrice('subtotal_incl_tax') === 0 ? getPrice('subtotal').priceFormat() : getPrice('subtotal_incl_tax').priceFormat() }}</span>
            </li>
            <li v-if="getPrice('discount_amount')">
              <span>{{ $t('Discount') }}</span>
              <span>{{ getPrice('discount_amount').priceFormat() }}</span>
            </li>
            <li>
              <span>{{ $t('Shipping costs') }}</span>
              <span :class="{ _free: shippingAmount === 0 }">{{ shippingAmount === 0 ? $t('Free') : shippingAmount.priceFormat() }}</span>
            </li>
            <li class="_grand">
              <span>{{ $t('Total incl. VAT') }}</span>
              <span>{{ getPrice('grand_total').priceFormat() }}</span>
            </li>
            <li>
              <span>{{ $t('Total excl. VAT') }}</span>
              <span>{{ grandTotalExclTax.priceFormat() }}</span>
            </li>
            <li>
              <span>{{ vatLabel }}</span>
              <span>{{ getPrice('tax_amount').priceFormat() }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="small-24 medium-12 large-24 cell">
      <h4>{{ deliveryHeading }}</h4>
      <div class="summary-block">
        <ul class="delivery__address" v-if="isPickup">
          <li v-if="pickupAddress['Name']"><strong>{{ pickupAddress['Name'] }}</strong></li>
          <li>
            {{ pickupAddress['Street'] }}
            {{ pickupAddress['HouseNr'] }}{{ pickupAddress['HouseNrExt'] || '' }}
          </li>
          <li>
            {{ pickupAddress['Zipcode'] }}
            {{ pickupAddress['City'] }}
          </li>
        </ul>
        <ul class="delivery__address" v-else>
          <li>
            {{ customerAddressInformation['prefix'] }}
            {{ customerAddressInformation['firstname'] }}
            {{ ((customerAddressInformation['middlename'] || '') + ' ' + (customerAddressInformation['lastname'] || '')).trim() }}
          </li>
          <li v-if="customerAddressInformation.hasOwnProperty('street')">
            {{ customerAddressInformation['street'][0] }}
            {{ customerAddressInformation['street'][1] }}
            {{ customerAddressInformation['street'][2] }}
          </li>
          <li>
            {{ customerAddressInformation['postcode'] }}
            {{ customerAddressInformation['city'] }}
          </li>
          <li>{{ customerEmail }}</li>
        </ul>
        <button type="button" class="_button _small _secondary" @click="gotoStep()">{{ deliveryChangeLabel }}</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import {Component, Vue} from 'vue-property-decorator';

  @Component({
    name: 'checkout-summary'
  })

  export default class extends Vue {
    private get cartTotals(): object {
      return this.$store.getters['CheckoutTotals/cartTotals'];
    }

    private get cartItems(): object {
      return this.$store.getters['CheckoutTotals/cartItems'];
    }

    private getPrice(priceKey): number {
      if (!this.cartTotals || typeof this.cartTotals[priceKey] === 'undefined') {
        return 0;
      }

      return Number(this.cartTotals[priceKey]) || 0;
    }

    private get shippingAmount(): number {
      const incl = this.getPrice('shipping_incl_tax');

      return incl > 0 ? incl : this.getPrice('shipping_amount');
    }

    private get grandTotalExclTax(): number {
      return this.getPrice('grand_total') - this.getPrice('tax_amount');
    }

    private get vatLabel(): string {
      if (this.getPrice('tax_amount') === 0 && this.getPrice('grand_total') > 0) {
        return String(this.$t('VAT reversed'));
      }

      return String(this.$t('VAT'));
    }

    private get totalsAvailable(): boolean {
      return this.$store.getters['CheckoutTotals/totalsAvailable'];
    }

    private get customerAddressInformation(): object {
      return this.$store.getters['CheckoutAddress/shippingAddress'];
    }

    private get customerEmail(): string {
      return this.$store.getters['CheckoutGlobal/customerEmail'] || '';
    }

    private get postnlDeliveryOption(): any {
      return this.$store.getters['CheckoutShipping/postnlDeliveryOption'];
    }

    private get isPickup(): boolean {
      return this.postnlDeliveryOption?.type === 'pickup' && !!this.postnlDeliveryOption?.LocationCode;
    }

    private get pickupAddress(): Record<string, string> {
      return this.postnlDeliveryOption?.address || {};
    }

    private get deliveryHeading(): string {
      return String(this.$t(
        this.isPickup
          ? 'You can pick up your order at'
          : 'Your order will be delivered on'
      ));
    }

    private get deliveryChangeLabel(): string {
      return String(this.$t(
        this.isPickup
          ? 'Change pickup point'
          : 'Change delivery address'
      ));
    }

    private gotoStep(): void {
      this.$store.dispatch('CheckoutGlobal/gotoStep', 1);
    }
  }
</script>
